import { Component, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ProjectDomainService } from '../domain-services/project-domain.service';
import { ApiService } from '../api.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, finalize, of, switchMap } from 'rxjs';

@Component({
  selector: 'app-generate-ids',
  imports: [MatFormFieldModule, MatButtonModule, RouterLink, MatInputModule],
  templateUrl: './generate-ids.html',
  styleUrl: './generate-ids.scss',
})
export class GenerateIds {
  private readonly projectDomain = inject(ProjectDomainService);
  private readonly api = inject(ApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);

  count = signal<number | null>(null);
  isSubmitting = signal(false);
  apiError = signal<string | null>(null);

  onCountChange(value: string) {
    const n = Number(value);
    if (Number.isNaN(n)) {
      this.count.set(null);
      return;
    }
    this.count.set(n);
  }

  add() {
    if (this.isSubmitting()) {
      return;
    }

    const currentCount = this.count();
    if (currentCount == null || currentCount < 0 || currentCount > 999) {
      const message = 'Please enter a number between 0 and 999.';
      this.apiError.set(message);
      this.snackBar.open(message, 'Close', { duration: 5000 });
      return;
    }

    const currentProject = this.projectDomain.currentProject();
    const projectId = currentProject?.id ?? this.route.snapshot.paramMap.get('projectId');

    if (!projectId) {
      const message = 'Missing project id.';
      this.apiError.set(message);
      this.snackBar.open(message, 'Close', { duration: 5000 });
      return;
    }

    this.isSubmitting.set(true);
    this.apiError.set(null);

    this.api
      .generateProjectIds(projectId, currentCount)
      .pipe(
        switchMap((response) => {
          const location = response.headers.get('Location');
          if (!location) {
            throw new Error('Missing Location header for generated IDs download.');
          }
          return this.api.downloadGeneratedIdsCsv(location).pipe(
            // pair blob with original count and a stable download name
            // (we keep using count from the outer scope)
            // eslint-disable-next-line rxjs/no-ignored-observable
            // @ts-ignore
            (source$) => source$, // no-op preserve
          );
        }),
        finalize(() => {
          this.isSubmitting.set(false);
        }),
      )
      .subscribe({
        next: (blob: Blob) => {
          const count = currentCount;

          this.projectDomain
            .loadProjectUnclaimedIds(projectId)
            .pipe(catchError(() => of([])))
            .subscribe();

          const url = URL.createObjectURL(blob);

          const ref = this.snackBar.open(
            `You have successfully generated ${count} new Ids.`,
            'Download',
            { duration: 8000 },
          );

          ref.onAction().subscribe(() => {
            const link = document.createElement('a');
            link.href = url;
            link.download = 'generated-ids.csv';
            link.click();
            URL.revokeObjectURL(url);
          });

          this.router.navigate(['..'], {
            relativeTo: this.route,
            state: {
              showLoading: false,
            },
          });
        },
        error: (err) => {
          const message = err?.error?.message || err?.message || 'Failed to generate IDs.';
          this.apiError.set(message);
          this.snackBar.open(message, 'Close', { duration: 5000 });
        },
      });
  }
}
